import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitListPrice,
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductPriceSavingsMessage from "@NA/Overrides/Components/ProductPriceSavingsMessage";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIColors from "@NA/Utils/ADIColors";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { AIProtonModel } from "@NA/Widgets/AIProton/AIProtonModel";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import { isPriceAvailable } from "@NA/Utils/Utility";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { isProductInventoryOnlyRole, priceForPIORole } from "@NA/Widgets/MyAccount/ADIMyAccount";

interface OwnProps {
    product: ProductContextModel | CartLineModel | AIProtonModel;
    currencySymbol?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: ProductPriceStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    currentPage: getCurrentPage(state).name,
    currentPageType: getCurrentPage(state).type,
    roles: state.context.session.userRoles,
    catalogSettings: state.context.settings.settingsCollection.catalogSettingSettings,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceWrapperCustom?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    invalidPriceText?: TypographyPresentationProps;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    msrPriceTextStyle?: TypographyPresentationProps
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    priceTextTotal?: TypographyPresentationProps;
}

interface PriceStylesCustom {
    unitMeasureDisplayStyles?: TypographyPresentationProps;
    priceContainer?: InjectableCss;
}

export const productPriceStyles: ProductPriceStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            -webkit-flex-direction: row;
            -ms-flex-direction: row;
            flex-direction: row;
            gap: 10px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            -webkit-flex-direction: column;
                            -ms-flex-direction: column;
                            flex-direction: column;
                            gap: 0;
                        `,
                        css`
                            -webkit-flex-direction: column;
                            -ms-flex-direction: column;
                            flex-direction: column;
                            gap: 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}

        `,
    },
    priceWrapperCustom: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 10px;
            width: 100%;
        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    priceWrapper: {
        css: css`
            ${wrapInContainerStyles}
        `,
    },
    price: {
        priceText: {
            weight: "bold",
            css: css`
                color: ${ADIColors.darkBlue4};
                font-size: 18px !important;
                font-weight: 900 !important;
                color: ${ADIColors.darkBlack4};
                display: flex;
                
                 ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                        font-size: 16px !important;
                    `,
                    css`
                        font-size: 16px !important;
                    `,
                    css`
                        font-size: 16px !important;
                    `,
                    css`
                        font-size: 16px !important;
                    `,
                    null,
                    ])}
            }
            `,
        },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasureText: {
            css: css`
                white-space: nowrap;
            `,
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    invalidPriceText: {
        color: "danger",
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 5px;
            ${wrapInContainerStyles}
        `,
    },
    secondaryPrice: {
        priceText: {
            weight: "bold",
            css: css`
                color: ${ADIColors.darkBlue4};
                font-size: 18px !important;
                font-weight: 900 !important;
            `,
        },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasureText: {
            css: css`
                white-space: nowrap;
            `,
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {
        italic: true,
        size: "12px",
    },   
    quoteMessage: {
        text: { weight: "bold" },
    },
};

const SectionWrapper = getStyledWrapper("section");

export const getunitOfMeasureDisplay = (uom: string = "") => {
    if (uom == null || uom == undefined || uom == "") {
        return "";
    } else {
        if (uom?.toLowerCase() === "each" || uom?.toLowerCase() === "ea") {
            return "";
        } else if (uom?.toLowerCase() === "package" || uom?.toLowerCase() === "pkg" || uom?.toLowerCase() === "pk") {
            return "/pkg";
        } else if (uom?.toLowerCase() === "pr" || uom?.toLowerCase() === "pair") {
            return "/pair";
        } else if (uom?.toLowerCase() === "ft" || uom?.toLowerCase() === "foot") {
            return "/foot";
        } else if (uom?.toLowerCase() === "rl" || uom?.toLowerCase() === "roll") {
            return "/roll";
        } else {
            return `/${uom}`;
        }
    }
};

const formatPrice = (num: string) => {
    // Trim whitespace from input
    const trimmedNum = num.trim();
  
    // Check if the trimmed string is a valid number
    if (!/^-?\d+(\.\d+)?$/.test(trimmedNum)) {
      return num; // Return original if it's not a valid number
    }
  
    const number = parseFloat(trimmedNum);
  
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0, 
      maximumFractionDigits: 2,
    }).format(number);
};

export const formatPriceToDisplay = (price: number, symbol = "$") => {
    const numericPrice = price;
    const priceStr = numericPrice.toFixed(2).toString();
    const [dollars, cents] = priceStr.split(".");
    return (
        <>
            <sup className="symbolText">{symbol}</sup>
            <span className="priceText">{formatPrice(dollars)}</span>
            <sup className="currencyText">{cents}</sup>
        </>
    );
};

export const formatPriceToDisplayforPIO = (catalogSettings: any, currencySymbol:any) => {
    const priceStr = priceForPIORole(catalogSettings);
    if (priceStr) {
        const pioPrice = priceStr.split(".");

        return (
            <>
                <sup className="symbolText">{currencySymbol}</sup>
                <span className="priceText">{pioPrice[0]}</span>
                <sup className="currencyText">{pioPrice[1]}</sup>
            </>
        );
    }
};

const ProductPrice = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = true,
    showSavingsAmount = true,
    showSavingsPercent = true,
    showInvalidPriceMessage = false,
    enableVat,
    vatPriceDisplay,
    extendedStyles,
    currentPage,
    currentPageType,
    roles,
    catalogSettings,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productPriceStyles, extendedStyles));

    const newWapper = currentPage?.match(/^(Product List|Product Details|My Lists|Category Details|Home)$/)
        ? styles.priceWrapperCustom
        : styles.wrapper;
    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.unitOfMeasure
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;

    const qtyOrdered = productContextModel?.product
        ? productContextModel.productInfo?.qtyOrdered
        : cartLineModel?.qtyOrdered;

    const pricing = "product" in pricingData ? pricingData.productInfo?.pricing : pricingData?.pricing;
    const pricingIsAvailable = isPriceAvailable(pricing, qtyOrdered);

    const allowZeroPricing =
        "product" in pricingData ? pricingData.product.allowZeroPricing : pricingData.allowZeroPricing;
    const isPromotionItem =
        "product" in pricingData ? false : "isPromotionItem" in pricingData ? pricingData.isPromotionItem : false;
    const isInvalidPrice =
        !quoteRequired &&
        !isPromotionItem &&
        !allowZeroPricing &&
        pricing?.unitNetPrice === 0 &&
        pricing?.unitRegularPrice === 0;

    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};
    
    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText}>{siteMessage("Pricing_SignInForPrice")}</Typography>
            </StyledWrapper>
        );
    }

    if ("product" in pricingData && pricingData.productInfo?.failedToLoadPricing) {
        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...priceStyles.errorText}>{siteMessage("RealTimePricing_PriceLoadFailed")}</Typography>
            </SectionWrapper>
        );
    }

    if (!pricingIsAvailable) {
        return null;
    }
    if (quoteRequired) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";

    return (
        <SectionWrapper {...newWapper}>    
            <StyledWrapper {...styles.priceWrapper}>
                <Price
                    currencySymbol={currencySymbol}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    enableVat={enableVat}
                    displayWithVat={displayVatForPrice}
                    styles={priceStyles}
                    currentPage={currentPage}
                    unitOfMeasureDisplay={unitOfMeasureDisplay}
                    currentPageType={currentPageType}
                    roles={roles}
                    catalogSettings={catalogSettings}
                />             
                {isInvalidPrice && showInvalidPriceMessage && (
                    <Typography as="p" {...styles.invalidPriceText}>
                        {translate("Invalid Price")}
                    </Typography>
                )}
            </StyledWrapper> 
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper}>
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        styles={secondaryPriceStyles}
                        unitOfMeasureDisplay={unitOfMeasureDisplay}
                        roles={roles}
                        catalogSettings={catalogSettings}
                    />
                </StyledWrapper>
            )}
            {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}
            {pricing && showSavings && (
                <ProductPriceSavingsMessage
                    pricing={pricing}
                    product={pricingData}
                    qtyOrdered={qtyOrdered || 1}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    currencySymbol={currencySymbol}
                    {...styles.savingsMessageText}
                    currentPage={currentPage}
                />
            )}
        </SectionWrapper>
    );
};

interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
    currentPage?: string;
    unitOfMeasureDisplay?: string;
    currentPageType?: string;
    roles: any;
    catalogSettings: any;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
    currentPage,
    currentPageType,
    unitOfMeasureDisplay,
    roles,
    catalogSettings,
}: PriceProps) => {
    const PriceStylesCustom: PriceStylesCustom = {
        unitMeasureDisplayStyles: {
            css: css`
                color: ${currentPage?.match(/^(Product List|Product Details|My Lists|Category Details|MyAccount|Home)$/)
                    ? ADIColors.gray4
                : ADIColors.darkBlack};
                font-size: ${currentPage?.match(/^(Product List|Product Details)$/) ? "12px" : "inherit"};
                margin: 0 3px;
                margin-left: 0;                                       
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-weight: ${currentPage?.match(
                                /^(Product List|Product Details|My Lists|Category Details|MyAccount|Home|Cart)$/,
                            )
                                ? "600"
                                : "900"};
                        `,
                        css`
                            font-weight: ${currentPage?.match(
                                /^(Product List|Product Details|My Lists|Category Details|MyAccount|Home|Cart)$/,
                            )
                                ? "600"
                                : "900"};
                        `,
                        css`
                            font-weight: ${currentPage?.match(
                                /^(Product List|Product Details|My Lists|Category Details|MyAccount|Home)$/,
                            )
                                ? "600"
                                : "900"};
                        `,
                        css`
                            font-weight: ${currentPage?.match(
                                /^(Product List|Product Details|My Lists|Category Details|MyAccount|Home)$/,
                            )
                                ? "600"
                                : "900"};
                        `,
                        css`
                            font-weight: ${currentPage?.match(
                                /^(Product List|Product Details|My Lists|Category Details|MyAccount|Home)$/,
                            )
                                ? "600"
                                : "900"};
                        `,
                    ])}
            `,
        },
        priceContainer: {
            css: css`
                display: flex;
                align-items: flex-start;
                flex-direction: column;
            `,
        },
    };

    let salePriceLabelPrefix = "";
    if (pricing && salePriceLabel) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);
        if (unitNetPrice < unitListPrice) {
            salePriceLabelPrefix = `${salePriceLabel} `;
        }
    }

    return (
        <>
            {pricing ? (
                <StyledWrapper data-test-selector="productPrice_Wrapper">
                    <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">               
                        {currentPageType == "CheckoutReviewAndSubmitPage" && (
                            <Typography {...styles.priceTextTotal}>Total:</Typography>
                        )}
                        {salePriceLabelPrefix}
                        {currentPage?.match(
                            /^(Product List|Product Details|My Lists|Category Details|MyAccount|Home)$/,
                        ) ? (
                            <>              
                                {!isProductInventoryOnlyRole(roles)
                                    ? enableVat && displayWithVat
                                        ? formatPriceToDisplay(
                                              getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).price,
                                          )
                                        : formatPriceToDisplay(getUnitNetPrice(pricing, qtyOrdered || 1).price)
                                    : formatPriceToDisplayforPIO(catalogSettings, currencySymbol)}
                            </>
                        ) : (
                            <>
                                {enableVat && displayWithVat
                                    ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                                    : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                            </>
                        )}
                        {currentPage?.match(/^(Product List|Product Details|My Lists|Category Details|Home|Cart)$/) &&
                            getunitOfMeasureDisplay(unitOfMeasureDisplay) ? (
                            <Typography className="unitofmeasureText" as={"sup"} {...PriceStylesCustom.unitMeasureDisplayStyles}>
                                {getunitOfMeasureDisplay(unitOfMeasureDisplay)}
                            </Typography>
                        ) : null}
                    </Typography>
                </StyledWrapper>
            ) : (
                <Typography {...styles.realTimeText} data-test-selector="productPrice_unitNetPrice">
                    {/* Below text might be required in future */}
                    {/* {currencySymbol} */}
                    {""}
                </Typography>
            )}
            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPrice);
